import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import WhatsAppButton from "@/components/WhatsAppButton";
import { CheckCircle2, Loader2, AlertCircle, ArrowLeft, Calendar } from "lucide-react";

export default function StoreSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState({ state: "polling", data: null });
  const [settings, setSettings] = useState({ calendly_url: "", calendar_enabled: false });
  const [product, setProduct] = useState(null);
  const attemptsRef = useRef(0);
  const stopRef = useRef(false);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!sessionId) { setStatus({ state: "error", data: null }); return; }

    const poll = async () => {
      if (stopRef.current) return;
      attemptsRef.current += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid" || data.payment_status === "complimentary") {
          setStatus({ state: "paid", data });
          if (data.product_id) {
            try { const p = await api.get(`/products/${data.product_id}`); setProduct(p.data); } catch (_) { /* ignore */ }
          }
          return;
        }
        if (data.status === "expired" || attemptsRef.current >= 12) {
          setStatus({ state: data.status === "expired" ? "expired" : "timeout", data });
          return;
        }
        setStatus({ state: "polling", data });
        setTimeout(poll, 2500);
      } catch {
        setStatus({ state: "error", data: null });
      }
    };
    poll();
    return () => { stopRef.current = true; };
  }, [sessionId]);

  const showCalendar = status.state === "paid"
    && settings.calendar_enabled
    && settings.calendly_url
    && product?.category === "service";

  return (
    <div className="bg-warmwhite text-charcoal min-h-screen" data-testid="store-success-page">
      <Nav />
      <main className="pt-40 pb-32">
        <div className="max-w-3xl mx-auto px-6 text-center">
          {status.state === "polling" && (
            <>
              <Loader2 className="w-12 h-12 text-gold mx-auto animate-spin" strokeWidth={1.2} />
              <h1 className="mt-8 font-serif text-4xl md:text-5xl font-light leading-tight">Confirming your payment…</h1>
              <p className="mt-4 text-charcoal/60">Hang tight — this usually takes a few seconds.</p>
            </>
          )}
          {status.state === "paid" && (
            <>
              <CheckCircle2 className="w-14 h-14 text-olive mx-auto" strokeWidth={1.2} />
              <h1 className="mt-8 font-serif text-4xl md:text-6xl font-light leading-tight" data-testid="success-title">
                Thank you. <span className="italic text-gold">Order received.</span>
              </h1>
              {product && (
                <p className="mt-6 text-charcoal/70 text-lg leading-relaxed">
                  <span className="font-serif italic">{product.name}</span> — {product.delivery || "I'll be in touch within 1 business day with next steps."}
                </p>
              )}

              {showCalendar && (
                <section className="mt-16 text-left" data-testid="success-calendly">
                  <div className="flex items-center gap-3 mb-4">
                    <Calendar className="w-5 h-5 text-gold" strokeWidth={1.5} />
                    <p className="text-[11px] uppercase tracking-widest-custom text-olive">Next step</p>
                  </div>
                  <h2 className="font-serif text-3xl md:text-4xl font-light leading-tight mb-3">Pick a time for your session.</h2>
                  <p className="text-charcoal/70 mb-8">{`Choose a slot that works for you. You'll get a calendar invite immediately.`}</p>
                  <div className="border border-taupe/40 bg-warmwhite overflow-hidden">
                    <iframe
                      title="Schedule with James"
                      src={`${settings.calendly_url}?hide_gdpr_banner=1&primary_color=c8a96a`}
                      width="100%"
                      height="720"
                      style={{ border: 0, minHeight: "720px" }}
                      loading="lazy"
                      data-testid="calendly-iframe"
                    />
                  </div>
                  <p className="mt-4 text-xs text-charcoal/55">
                    Can't see the scheduler? <a href={settings.calendly_url} target="_blank" rel="noreferrer" className="text-gold hover:underline">Open it in a new tab →</a>
                  </p>
                </section>
              )}

              {!showCalendar && (
                <Link to="/" className="mt-12 inline-flex items-center gap-2 bg-charcoal text-warmwhite px-8 py-4 text-[11px] uppercase tracking-widest-custom hover:bg-gold hover:text-charcoal transition-colors" data-testid="success-back-home">
                  <ArrowLeft className="w-4 h-4" /> Back to home
                </Link>
              )}
            </>
          )}
          {(status.state === "expired" || status.state === "timeout" || status.state === "error") && (
            <>
              <AlertCircle className="w-12 h-12 text-charcoal mx-auto" strokeWidth={1.2} />
              <h1 className="mt-8 font-serif text-4xl md:text-5xl font-light leading-tight">{`Something didn't go through.`}</h1>
              <p className="mt-4 text-charcoal/60">
                {status.state === "expired" ? "The checkout session expired." : `We couldn't confirm your payment in time. If you were charged, please email hello@jamesone.co.`}
              </p>
              <Link to="/store" className="mt-10 inline-flex items-center gap-2 border border-charcoal px-8 py-4 text-[11px] uppercase tracking-widest-custom hover:bg-charcoal hover:text-warmwhite transition-colors">
                Back to store
              </Link>
            </>
          )}
        </div>
      </main>
      <Footer />
      <WhatsAppButton />
    </div>
  );
}
